import { isApolloError } from "@apollo/client";
import { ServerError } from "@flaminem/utils";
import { isAxiosError } from "axios";
import { useCallback } from "react";

import { ErrorCode } from "src/graphql/utils/errorBuilder";
import { useT } from "src/internationalization";

/** Mutation errors are supposed to always display title "Server Error" and message from backend */
export const useMapToMutationError = () => {
  const t = useT();

  const mapToMutationError = useCallback(
    <TError extends Error | null | undefined>(
      error: TError
    ): TError extends null | undefined
      ? ServerError<ErrorCode> | null | undefined
      : ServerError<ErrorCode> => {
      if (!error) {
        return error as unknown as TError extends null | undefined
          ? ServerError<ErrorCode> | null | undefined
          : ServerError<ErrorCode>;
      }

      if (isApolloError(error)) {
        return {
          title: t("error.server.title"),
          message: error.message,
          type: "Error",
          code: "SERVER-ERROR",
          error,
        };
      }

      if (isAxiosError(error)) {
        const errorMessage: string =
          error.response?.data && typeof error.response.data === "string"
            ? error.response.data
            : error.message;

        return {
          title: t("error.server.title"),
          message: errorMessage,
          type: "Error",
          code: "SERVER-ERROR",
          error,
        };
      }

      return {
        title: t("error.server.title"),
        message: error.message,
        type: "Error",
        code: "SERVER-ERROR",
        error,
      };
    },
    [t]
  );

  return mapToMutationError;
};
